import Cookies from 'js-cookie'

const DEVICE_ID_COOKIE_KEY = 'ld_device_id'
const DEVICE_ID_COOKIE_DOMAIN = 'pleo.io'
const DEVICE_ID_COOKIE_EXPIRY_DAYS = 365

type ConsentGate = {
    hasAdvertisingConsent: () => boolean
    onConsentChange: (callback: () => void) => void
}

let cachedDeviceId: string | undefined
let persisted = false
let listenerRegistered = false

const persistDeviceId = (consent: ConsentGate) => {
    if (persisted || !cachedDeviceId || !consent.hasAdvertisingConsent()) return

    Cookies.set(DEVICE_ID_COOKIE_KEY, cachedDeviceId, {
        expires: DEVICE_ID_COOKIE_EXPIRY_DAYS,
        domain: DEVICE_ID_COOKIE_DOMAIN,
    })
    persisted = true
}

/**
 * Resolves a stable per-visitor id for the LaunchDarkly `device` context, shared across
 * pleo.io and app.pleo.io via a first-party cookie so cross-stack experiments can use the
 * same randomization unit.
 *
 * Synchronous and module-cached (not a hook): meant to be called directly in
 * `LaunchDarklyProvider`'s render body, before `LDProvider` is rendered, so the id is already
 * resolved by the time the LD SDK client initialises — no separate resolution step, no
 * throwaway anonymous identity.
 *
 * Client-only: `document` doesn't exist during Gatsby SSR (Node), so this returns `''` and
 * caches nothing there. On the client, the first call reads the `ld_device_id` cookie (if
 * present) or generates one with `crypto.randomUUID()`, then caches it in module scope for the
 * life of the page — every later call (re-renders, remounts) reuses the same id.
 *
 * The id always exists in-memory for the session, regardless of consent, so LD can still
 * evaluate flags. It's only persisted to the `ld_device_id` cookie (readable from
 * app.pleo.io) once `consent.hasAdvertisingConsent()` is true — at most once, and an existing
 * id is never regenerated. A `consent.onConsentChange` listener (registered once) persists the
 * same cached id the moment consent is later granted, mirroring
 * `packages/analytics/src/session.ts`'s `queueSessionTrackingCookies`.
 */
export function resolveDeviceId(consent: ConsentGate): string {
    if (typeof document === 'undefined') return ''

    if (!cachedDeviceId) {
        cachedDeviceId = Cookies.get(DEVICE_ID_COOKIE_KEY) ?? crypto.randomUUID()
    }

    persistDeviceId(consent)

    if (!listenerRegistered) {
        listenerRegistered = true
        consent.onConsentChange(() => persistDeviceId(consent))
    }

    return cachedDeviceId
}
